// In-app guide for the offer page ("/offer", "/offer/:offerId"). Pure data — see help/types.ts.
// Change this page (or a rule a step quotes) → update this file in the same commit.
import { OFFER_STATUSES, type PageHelp } from "../../help/types";

export const offerHelp: PageHelp = {
  title: "Offer",
  intro:
    'One MTPL or CASCO offer from start to invoice. Fill in the vehicle and the client on "General Information" (Prefill does most of it), then on "Offer" choose the start date and insurers and press "Create Offer" to get quotes and documents. Afterwards you can adjust quotes, send the offer by e-mail and create the invoice for the quote the client chose.',
  sections: [
    "Getting around",
    "Prefill the vehicle",
    "Vehicle data",
    "Client data",
    "Documents and e-mail",
    "Insurance and insurers",
    "Quotes",
    "Invoice",
  ],
  contexts: {
    "general-tab": 'Open the "General Information" tab.',
    "offer-tab": 'Open the "Offer" tab.',
    "casco-general": 'Only on a CASCO offer, on the "General Information" tab.',
    "company-client": 'On "General Information", set Client Type to company.',
    "person-client": 'On "General Information", set Client Type to private person.',
    "new-offer-tab": 'Only on a new offer before "Create Offer", on the "Offer" tab.',
    "casco-new-offer-tab": 'Only on a new CASCO offer before "Create Offer", on the "Offer" tab.',
    "mtpl-offer-tab": 'Only on an MTPL offer, on the "Offer" tab.',
    "created-offer": 'Only on a created offer, on the "Offer" tab.',
    "offer-not-invoiced": 'Only on a created offer without an invoice, on the "Offer" tab.',
    "mtpl-not-invoiced": 'Only on a created MTPL offer without an invoice, on the "Offer" tab.',
    "quote-edits": 'On a created offer without an invoice, edit a premium or exclude an insurer on the "Offer" tab.',
    "email-dialog": 'On the "Offer" tab, click the envelope icon next to the offer PDF.',
    "invoice-dialog": 'On the "Offer" tab, select a premium in the quotes table and click "Create Invoice".',
  },
  // The guide opens the tab / dialog itself, and when this offer cannot show the step it goes to one that can
  // (asking first when that means leaving a new, unsaved offer). It never changes the offer's data (client type,
  // chosen premium, edited quotes).
  reveal: {
    "general-tab": ['[data-help="offer-tab-general"]'],
    "offer-tab": ['[data-help="offer-tab-offer"]'],
    "casco-general": { press: ['[data-help="offer-tab-general"]'], newOffer: "casco" },
    "company-client": ['[data-help="offer-tab-general"]'],
    "person-client": ['[data-help="offer-tab-general"]'],
    "new-offer-tab": { press: ['[data-help="offer-tab-offer"]'], newOffer: "mtpl" },
    "casco-new-offer-tab": { press: ['[data-help="offer-tab-offer"]'], newOffer: "casco" },
    "mtpl-offer-tab": { press: ['[data-help="offer-tab-offer"]'], newOffer: "mtpl" },
    "created-offer": { press: ['[data-help="offer-tab-offer"]'], offer: { action: "open" } },
    "offer-not-invoiced": {
      press: ['[data-help="offer-tab-offer"]'],
      offer: { statuses: OFFER_STATUSES, action: "open" },
    },
    "mtpl-not-invoiced": {
      press: ['[data-help="offer-tab-offer"]'],
      offer: { statuses: OFFER_STATUSES, product: "MTPL", action: "open" },
    },
    "quote-edits": { press: ['[data-help="offer-tab-offer"]'], offer: { statuses: OFFER_STATUSES, action: "open" } },
    "email-dialog": {
      press: ['[data-help="offer-tab-offer"]', '[data-help="offer-send-offer-email"]'],
      offer: { statuses: OFFER_STATUSES, action: "open" },
    },
    "invoice-dialog": {
      press: ['[data-help="offer-tab-offer"]', '[data-help="offer-create-invoice-button"]'],
      offer: { statuses: OFFER_STATUSES, action: "open" },
    },
  },
  tips: [
    'Once an offer is created, its vehicle, client and start date are locked. To quote again with other data, use "Duplicate Offer" in the offers list.',
    'Changes to quotes (edited premiums, excluded insurers) are only kept after "Update Offer". While changes are pending, "Create Invoice" is hidden.',
    "When an invoice exists the quotes can no longer be edited and the offer can no longer be e-mailed from here — send the invoice instead.",
  ],
  steps: [
    // --- Getting around --------------------------------------------------------------
    {
      selector: '[data-help="offer-back"]',
      title: "Back to the offers list",
      body: "Returns to the list of offers. An offer that has not been created yet is not saved.",
      section: "Getting around",
      i18n: { lv: { title: "Atpakaļ uz piedāvājumu sarakstu", body: "Atgriežas piedāvājumu sarakstā. Piedāvājums, kas vēl nav izveidots, netiek saglabāts." } },
    },
    {
      selector: '[data-help="offer-tab-general"]',
      title: "General Information tab",
      body: "The vehicle and the client. A red number on the tab counts the fields that stop a calculation; open the tab to see them marked.",
      section: "Getting around",
      i18n: { lv: { title: "Cilne “Vispārīgā informācija”", body: "Transportlīdzeklis un klients. Sarkans skaitlis uz cilnes rāda, cik lauku kavē aprēķinu; atveriet cilni, lai redzētu tos iezīmētus." } },
    },
    {
      selector: '[data-help="offer-tab-offer"]',
      title: "Offer tab",
      body: "Start date, insurers, the quotes, the generated documents and the invoice. An existing offer opens here. You cannot switch tabs while a calculation or prefill is running.",
      section: "Getting around",
      i18n: { lv: { title: "Cilne “Piedāvājums”", body: "Sākuma datums, apdrošinātāji, kotācijas, izveidotie dokumenti un rēķins. Esošs piedāvājums atveras šeit. Kamēr notiek aprēķins vai aizpildīšana, cilnes pārslēgt nevar." } },
    },
    {
      selector: '[data-help="offer-header-info"]',
      title: "Offer number, product and status",
      body: 'Shows the offer number (or "NEW" before it is created), the invoice and group invoice numbers when they exist, the product and the current status. Hover the info icon for who requested and changed the offer and when.',
      section: "Getting around",
      i18n: { lv: { title: "Piedāvājuma numurs, produkts un statuss", body: "Rāda piedāvājuma numuru (vai “NEW”, kamēr tas nav izveidots), rēķina un grupas rēķina numurus, ja tādi ir, produktu un pašreizējo statusu. Novietojiet peli virs informācijas ikonas, lai redzētu, kas un kad piedāvājumu pieprasīja un mainīja." } },
    },
    {
      selector: '[data-help="offer-marked-as-error"]',
      title: "Offer marked as error",
      body: "Shown when an administrator marked the offer as error, with who did it and when. You can look through the offer, but nothing can be calculated, saved, sent, invoiced, paid or issued until an administrator unmarks it in the offers list.",
      section: "Getting around",
      optional: true,
      i18n: { lv: { title: "Piedāvājums atzīmēts kā kļūdains", body: "Redzams, ja administrators piedāvājumu ir atzīmējis kā kļūdainu, kopā ar to, kas un kad to izdarīja. Piedāvājumu var apskatīt, taču neko nevar aprēķināt, saglabāt, nosūtīt, izrakstīt rēķinu, apmaksāt vai izsniegt, kamēr administrators piedāvājumu sarakstā atzīmi nenoņem." } },
    },
    {
      selector: '[data-help="app-help-launcher"]',
      title: "The page guide",
      body: 'This button, in the top bar on every page that has a guide. It opens the manual for the page you are on — this panel — with "Show me around" for a tour of what is on screen, "Point at a field" to click a control and read what it does, and "Ask" for questions about any screen.',
      section: "Getting around",
      i18n: { lv: { title: "Lapas ceļvedis", body: "Šī poga augšējā joslā ir katrā lapā, kurai ir ceļvedis. Tā atver pašreizējās lapas rokasgrāmatu — šo paneli — ar “Ekskursija pa lapu”, lai apskatītu ekrānā redzamo, “Norādīt lauku”, lai noklikšķinātu uz vadīklas un izlasītu, ko tā dara, un “Jautāt”, lai uzdotu jautājumus par jebkuru ekrānu." } },
    },
    {
      selector: '[data-help="app-notifications"]',
      title: "Notifications",
      body: "Messages that popped up during this session — mostly errors returned by the server — are collected here, and the number counts them. Open the panel to read one again or to clear them; the list starts empty after a reload.",
      section: "Getting around",
      i18n: { lv: { title: "Paziņojumi", body: "Šeit tiek apkopoti šīs sesijas laikā parādītie ziņojumi — galvenokārt servera atgrieztās kļūdas —, un skaitlis rāda to skaitu. Atveriet paneli, lai ziņojumu izlasītu vēlreiz vai tos notīrītu; pēc lapas pārlādes saraksts ir tukšs." } },
    },
    {
      selector: '[data-help="app-user-manual"]',
      title: "Download the user manual",
      body: "Downloads the complete QTG user manual as a PDF, with screenshots of every page. You get it in the language selected in Settings: Latvian when the app is in Latvian, English otherwise.",
      section: "Getting around",
      i18n: { lv: { title: "Lejupielādēt lietotāja rokasgrāmatu", body: "Lejupielādē pilnu QTG lietotāja rokasgrāmatu PDF formātā ar katras lapas ekrānuzņēmumiem. To saņemat iestatījumos izvēlētajā valodā: latviski, ja lietotne ir latviešu valodā, citādi angliski." } },
    },

    // --- Prefill the vehicle -------------------------------------------------------
    {
      selector: '[data-help="offer-general-plate"]',
      title: "Licence plate number",
      body: "One of the four keys Prefill can search by. Enter it as on the registration certificate.",
      section: "Prefill the vehicle",
      context: "general-tab",
      i18n: { lv: { title: "Numura zīme", body: "Viena no četrām atslēgām, pēc kurām var meklēt “Aizpildīt”. Ievadiet to tāpat kā reģistrācijas apliecībā." } },
    },
    {
      selector: '[data-help="offer-general-vin"]',
      title: "Vehicle identification number (VIN)",
      body: "The vehicle identification number — a second key for Prefill, and later used to match the issued policy.",
      section: "Prefill the vehicle",
      context: "general-tab",
      i18n: { lv: { title: "Transportlīdzekļa identifikācijas numurs (VIN)", body: "Transportlīdzekļa identifikācijas numurs — otrā atslēga funkcijai “Aizpildīt”, ko vēlāk izmanto arī izsniegtās polises sasaistei." } },
    },
    {
      selector: '[data-help="offer-general-certificate"]',
      title: "Registration certificate number",
      body: "The number of the technical passport (registration certificate) — a third key for Prefill.",
      section: "Prefill the vehicle",
      context: "general-tab",
      i18n: { lv: { title: "Reģistrācijas apliecības numurs", body: "Tehniskās pases (reģistrācijas apliecības) numurs — trešā atslēga funkcijai “Aizpildīt”." } },
    },
    {
      selector: '[data-help="offer-general-owner-code"]',
      title: "Owner code",
      body: "The owner's personal or company registration code — the fourth key for Prefill. When the register returns a client code, the client is also looked up in GOS.",
      section: "Prefill the vehicle",
      context: "general-tab",
      i18n: { lv: { title: "Īpašnieka kods", body: "Īpašnieka personas kods vai uzņēmuma reģistrācijas numurs — ceturtā atslēga funkcijai “Aizpildīt”. Ja reģistrs atgriež klienta kodu, klients tiek uzmeklēts arī GOS." } },
    },
    {
      selector: '[data-help="offer-general-leased"]',
      title: "Vehicle is leased",
      body: "Tick when the vehicle is leased. The owner's e-mail and phone then become required in Vehicle Data.",
      section: "Prefill the vehicle",
      context: "general-tab",
      i18n: { lv: { title: "Transportlīdzeklis ir līzingā", body: "Atzīmējiet, ja transportlīdzeklis ir līzingā. Tad sadaļā “Transportlīdzekļa dati” īpašnieka e-pasts un tālrunis kļūst obligāti." } },
    },
    {
      selector: '[data-help="offer-general-prefill-button"]',
      title: "Prefill vehicle and client",
      body: "Looks up the vehicle with whichever of plate, VIN, certificate number or owner code you entered (at least one is needed) and fills Vehicle Data — and Client Data when the owner is found in GOS. Check the result before calculating. Not available on an offer that was already created.",
      section: "Prefill the vehicle",
      context: "general-tab",
      i18n: { lv: { title: "Aizpildīt transportlīdzekli un klientu", body: "Uzmeklē transportlīdzekli pēc ievadītās numura zīmes, VIN, apliecības numura vai īpašnieka koda (vajadzīgs vismaz viens) un aizpilda transportlīdzekļa datus — un klienta datus, ja īpašnieks atrasts GOS. Pirms aprēķina pārbaudiet rezultātu. Nav pieejams jau izveidotam piedāvājumam." } },
    },

    // --- Vehicle data -----------------------------------------------------------------
    {
      selector: '[data-help="offer-general-vehicle-make"]',
      title: "Vehicle make",
      body: "Required. Filled by Prefill; check it and correct it by hand if it is missing or wrong.",
      section: "Vehicle data",
      context: "general-tab",
      i18n: { lv: { title: "Transportlīdzekļa marka", body: "Obligāti. Aizpilda “Aizpildīt”; pārbaudiet to un, ja tā trūkst vai ir nepareiza, labojiet manuāli." } },
    },
    {
      selector: '[data-help="offer-general-vehicle-model"]',
      title: "Vehicle model",
      body: "Required. The model as it appears in the registration certificate.",
      section: "Vehicle data",
      context: "general-tab",
      i18n: { lv: { title: "Transportlīdzekļa modelis", body: "Obligāti. Modelis tāds, kā norādīts reģistrācijas apliecībā." } },
    },
    {
      selector: '[data-help="offer-general-object-group"]',
      title: "Object group",
      body: "The legal vehicle group. Choosing an Object Group Type sets the group for you; changing the group clears a type that no longer fits.",
      section: "Vehicle data",
      context: "general-tab",
      i18n: { lv: { title: "Objekta grupa", body: "Transportlīdzekļa juridiskā grupa. Izvēloties objekta grupas tipu, grupa tiek iestatīta automātiski; mainot grupu, vairs neatbilstošs tips tiek notīrīts." } },
    },
    {
      selector: '[data-help="offer-general-object-group-type"]',
      title: "Object group type",
      body: "The vehicle type within the group; the list is filtered by the selected Object Group.",
      section: "Vehicle data",
      context: "general-tab",
      i18n: { lv: { title: "Objekta grupas tips", body: "Transportlīdzekļa tips grupas ietvaros; saraksts ir filtrēts pēc izvēlētās objekta grupas." } },
    },
    {
      selector: '[data-help="offer-general-transport-type"]',
      title: "Vehicle transport type",
      body: "The vehicle's transport type, chosen from the list shared with the insurers.",
      section: "Vehicle data",
      context: "general-tab",
      i18n: { lv: { title: "Transportlīdzekļa transporta veids", body: "Transportlīdzekļa transporta veids, ko izvēlas no saraksta, kas kopīgs ar apdrošinātājiem." } },
    },
    {
      selector: '[data-help="offer-general-fuel-type"]',
      title: "Fuel type",
      body: "The vehicle's power source (petrol, diesel, electric and so on), from the list.",
      section: "Vehicle data",
      context: "general-tab",
      i18n: { lv: { title: "Degvielas tips", body: "Transportlīdzekļa enerģijas avots (benzīns, dīzelis, elektrība u. c.), ko izvēlas no saraksta." } },
    },
    {
      selector: '[data-help="offer-general-engine-capacity"]',
      title: "Engine capacity",
      body: "Required, in cubic centimetres as on the registration certificate.",
      section: "Vehicle data",
      context: "general-tab",
      i18n: { lv: { title: "Dzinēja tilpums", body: "Obligāti, kubikcentimetros, kā norādīts reģistrācijas apliecībā." } },
    },
    {
      selector: '[data-help="offer-general-power"]',
      title: "Engine power",
      body: "Required, in kilowatts as on the registration certificate.",
      section: "Vehicle data",
      context: "general-tab",
      i18n: { lv: { title: "Dzinēja jauda", body: "Obligāti, kilovatos, kā norādīts reģistrācijas apliecībā." } },
    },
    {
      selector: '[data-help="offer-general-production-year"]',
      title: "Production year",
      body: "Required. The year the vehicle was made, as on the registration certificate.",
      section: "Vehicle data",
      context: "general-tab",
      i18n: { lv: { title: "Ražošanas gads", body: "Obligāti. Gads, kurā transportlīdzeklis izgatavots, kā norādīts reģistrācijas apliecībā." } },
    },
    {
      selector: '[data-help="offer-general-is-new"]',
      title: "New vehicle",
      body: "Tick for a vehicle that has not been registered before.",
      section: "Vehicle data",
      context: "general-tab",
      i18n: { lv: { title: "Jauns transportlīdzeklis", body: "Atzīmējiet transportlīdzeklim, kas iepriekš nav bijis reģistrēts." } },
    },
    {
      selector: '[data-help="offer-general-keyless"]',
      title: "Keyless access",
      body: "Tick when the vehicle opens and starts without a mechanical key.",
      section: "Vehicle data",
      context: "general-tab",
      i18n: { lv: { title: "Bezatslēgas piekļuve", body: "Atzīmējiet, ja transportlīdzekli var atvērt un iedarbināt bez mehāniskas atslēgas." } },
    },
    {
      selector: '[data-help="offer-general-body-type"]',
      title: "Body type",
      body: "The vehicle's body style, chosen from the list.",
      section: "Vehicle data",
      context: "general-tab",
      i18n: { lv: { title: "Virsbūves tips", body: "Transportlīdzekļa virsbūves veids, ko izvēlas no saraksta." } },
    },
    {
      selector: '[data-help="offer-general-usage-type"]',
      title: "Vehicle usage type",
      body: "How the vehicle is used. The options are shown in Latvian in both languages, as in the Latvian list they come from.",
      section: "Vehicle data",
      context: "general-tab",
      i18n: { lv: { title: "Transportlīdzekļa izmantošanas veids", body: "Kā transportlīdzeklis tiek izmantots. Iespējas abās valodās ir latviski, tāpat kā latviskajā sarakstā, no kura tās nāk." } },
    },
    {
      selector: '[data-help="offer-general-gross-weight"]',
      title: "Maximum gross weight",
      body: "Required, in kilograms as on the registration certificate.",
      section: "Vehicle data",
      context: "general-tab",
      i18n: { lv: { title: "Maksimālā pilnā masa", body: "Obligāti, kilogramos, kā norādīts reģistrācijas apliecībā." } },
    },
    {
      selector: '[data-help="offer-general-vehicle-value"]',
      title: "Vehicle value",
      body: "Required for CASCO: the insured value of the vehicle in euro. Only shown on CASCO offers.",
      section: "Vehicle data",
      context: "casco-general",
      i18n: { lv: { title: "Transportlīdzekļa vērtība", body: "Obligāti KASKO: transportlīdzekļa apdrošinājuma vērtība eiro. Redzama tikai KASKO piedāvājumos." } },
    },
    {
      selector: '[data-help="offer-general-other-driver-age"]',
      title: "Other driver's age",
      body: "Required for CASCO: the age of the other driver who will use the vehicle. Filled from the client's age when left empty, and the other way round.",
      section: "Vehicle data",
      context: "casco-general",
      i18n: { lv: { title: "Cita vadītāja vecums", body: "Obligāti KASKO: tā cita vadītāja vecums, kurš izmantos transportlīdzekli. Ja lauks ir tukšs, to aizpilda no klienta vecuma, un otrādi." } },
    },
    {
      selector: '[data-help="offer-general-other-driver-experience"]',
      title: "Other driver's experience",
      body: "Required for CASCO: years of driving experience of that driver. Synchronised with the client's driving experience in the same way as the age.",
      section: "Vehicle data",
      context: "casco-general",
      i18n: { lv: { title: "Cita vadītāja pieredze", body: "Obligāti KASKO: šī vadītāja braukšanas pieredze gados. Tiek sinhronizēta ar klienta braukšanas pieredzi tāpat kā vecums." } },
    },
    {
      selector: '[data-help="offer-general-coverage-territory"]',
      title: "Coverage territory",
      body: "Required for CASCO: where the cover applies. Europe without the CIS is preselected.",
      section: "Vehicle data",
      context: "casco-general",
      i18n: { lv: { title: "Darbības teritorija", body: "Obligāti KASKO: kur darbojas segums. Pēc noklusējuma izvēlēta Eiropa bez NVS." } },
    },
    {
      selector: '[data-help="offer-general-owner-name"]',
      title: "Owner name",
      body: "Required. The registered owner — for a leased vehicle this is usually the leasing company, not the client.",
      section: "Vehicle data",
      context: "general-tab",
      i18n: { lv: { title: "Īpašnieka vārds vai nosaukums", body: "Obligāti. Reģistrētais īpašnieks — līzingā esošam transportlīdzeklim parasti tas ir līzinga uzņēmums, nevis klients." } },
    },
    {
      selector: '[data-help="offer-general-owner-email"]',
      title: "Owner e-mail",
      body: "The owner's e-mail address. Required when the vehicle is leased.",
      section: "Vehicle data",
      context: "general-tab",
      i18n: { lv: { title: "Īpašnieka e-pasts", body: "Īpašnieka e-pasta adrese. Obligāta, ja transportlīdzeklis ir līzingā." } },
    },
    {
      selector: '[data-help="offer-general-owner-phone"]',
      title: "Owner phone",
      body: "The owner's phone number. Required when the vehicle is leased.",
      section: "Vehicle data",
      context: "general-tab",
      i18n: { lv: { title: "Īpašnieka tālrunis", body: "Īpašnieka tālruņa numurs. Obligāts, ja transportlīdzeklis ir līzingā." } },
    },

    // --- Client data ----------------------------------------------------------------
    {
      selector: '[data-help="offer-general-client-search"]',
      title: "Find the client in GOS",
      body: "Opens the GOS client search: type a name or code and click a result to fill all client fields. A blue \"Existing in GOS\" badge then marks the client as linked. Not available once the offer is created.",
      section: "Client data",
      context: "general-tab",
      i18n: { lv: { title: "Atrast klientu GOS", body: "Atver GOS klientu meklēšanu: ierakstiet nosaukumu vai kodu un noklikšķiniet uz rezultāta, lai aizpildītu visus klienta laukus. Zila zīme “Pastāv GOS” pēc tam apzīmē, ka klients ir sasaistīts. Nav pieejams, kad piedāvājums jau izveidots." } },
    },
    {
      selector: '[data-help="offer-general-client-type"]',
      title: "Client type",
      body: "Required. Company or private person — it switches the name field between a company name and first and last name.",
      section: "Client data",
      context: "general-tab",
      i18n: { lv: { title: "Klienta tips", body: "Obligāti. Uzņēmums vai fiziska persona — tas pārslēdz nosaukuma lauku starp uzņēmuma nosaukumu un vārdu un uzvārdu." } },
    },
    {
      selector: '[data-help="offer-general-client-name"]',
      title: "Company name",
      body: "Required for a company client: the registered company name as it should appear on the offer and the invoice.",
      section: "Client data",
      context: "company-client",
      i18n: { lv: { title: "Uzņēmuma nosaukums", body: "Obligāti klientam — uzņēmumam: reģistrētais uzņēmuma nosaukums tāds, kādam jābūt piedāvājumā un rēķinā." } },
    },
    {
      selector: '[data-help="offer-general-client-first-name"]',
      title: "First name",
      body: "Required for a private client, as it should appear on the offer and the invoice.",
      section: "Client data",
      context: "person-client",
      i18n: { lv: { title: "Vārds", body: "Obligāti fiziskai personai — tāds, kādam jābūt piedāvājumā un rēķinā." } },
    },
    {
      selector: '[data-help="offer-general-client-last-name"]',
      title: "Last name",
      body: "Required for a private client, as it should appear on the offer and the invoice.",
      section: "Client data",
      context: "person-client",
      i18n: { lv: { title: "Uzvārds", body: "Obligāti fiziskai personai — tāds, kādam jābūt piedāvājumā un rēķinā." } },
    },
    {
      selector: '[data-help="offer-general-client-code"]',
      title: "Client code",
      body: "Required: company registration number or personal code. Changing it after picking a client from GOS removes the GOS link.",
      section: "Client data",
      context: "general-tab",
      i18n: { lv: { title: "Klienta kods", body: "Obligāti: uzņēmuma reģistrācijas numurs vai personas kods. Ja to maina pēc klienta izvēles no GOS, sasaiste ar GOS tiek noņemta." } },
    },
    {
      selector: '[data-help="offer-general-client-address"]',
      title: "Client address",
      body: "Required. The client's postal address, as it should appear on the documents.",
      section: "Client data",
      context: "general-tab",
      i18n: { lv: { title: "Klienta adrese", body: "Obligāti. Klienta pasta adrese tāda, kādai jābūt dokumentos." } },
    },
    {
      selector: '[data-help="offer-general-client-email"]',
      title: "Client e-mail",
      body: "Required. The offer e-mail is addressed here by default; you can change the recipient before sending.",
      section: "Client data",
      context: "general-tab",
      i18n: { lv: { title: "Klienta e-pasts", body: "Obligāti. Piedāvājuma e-pasts pēc noklusējuma tiek adresēts šeit; pirms sūtīšanas adresātu var mainīt." } },
    },
    {
      selector: '[data-help="offer-general-client-phone"]',
      title: "Client phone",
      body: "Required. The client's contact phone number for this offer.",
      section: "Client data",
      context: "general-tab",
      i18n: { lv: { title: "Klienta tālrunis", body: "Obligāti. Klienta kontakttālrunis šim piedāvājumam." } },
    },
    {
      selector: '[data-help="offer-general-client-resident"]',
      title: "Resident",
      body: "Ticked by default. Untick for a client who is not a Latvian resident.",
      section: "Client data",
      context: "general-tab",
      i18n: { lv: { title: "Rezidents", body: "Pēc noklusējuma atzīmēts. Noņemiet atzīmi klientam, kurš nav Latvijas rezidents." } },
    },
    {
      selector: '[data-help="offer-general-client-age"]',
      title: "Client age",
      body: "The client's age in years. On a CASCO offer it is copied to the other driver's age when that is empty, and the other way round.",
      section: "Client data",
      context: "general-tab",
      i18n: { lv: { title: "Klienta vecums", body: "Klienta vecums gados. KASKO piedāvājumā tas tiek pārkopēts uz cita vadītāja vecumu, ja tas ir tukšs, un otrādi." } },
    },
    {
      selector: '[data-help="offer-general-client-experience"]',
      title: "Driving experience",
      body: "The client's years of driving experience; on CASCO synchronised with the other driver's experience like the age.",
      section: "Client data",
      context: "general-tab",
      i18n: { lv: { title: "Braukšanas pieredze", body: "Klienta braukšanas pieredze gados; KASKO tā tiek sinhronizēta ar cita vadītāja pieredzi tāpat kā vecums." } },
    },
    {
      selector: '[data-help="offer-general-continue-button"]',
      title: "Continue to the Offer tab",
      body: "Moves to the Offer tab to choose the start date and insurers. Only shown on a new offer.",
      section: "Client data",
      context: "general-tab",
      i18n: { lv: { title: "Turpināt uz cilni “Piedāvājums”", body: "Pāriet uz cilni “Piedāvājums”, lai izvēlētos sākuma datumu un apdrošinātājus. Redzams tikai jaunam piedāvājumam." } },
    },

    // --- Insurance and insurers ------------------------------------------------------
    {
      selector: '[data-help="offer-insurance-start-date"]',
      title: "Insurance start date",
      body: "Required. Tomorrow is suggested. Locked once the offer is created.",
      section: "Insurance and insurers",
      context: "offer-tab",
      i18n: { lv: { title: "Apdrošināšanas sākuma datums", body: "Obligāti. Tiek ieteikta rītdiena. Pēc piedāvājuma izveides tas ir bloķēts." } },
    },
    {
      selector: '[data-help="offer-insurance-previous-end-date"]',
      title: "Previous insurance end date",
      body: "MTPL only, optional: the date the vehicle's current MTPL policy ends, if there is one.",
      section: "Insurance and insurers",
      context: "mtpl-offer-tab",
      i18n: { lv: { title: "Iepriekšējās apdrošināšanas beigu datums", body: "Tikai OCTA, nav obligāts: datums, kad beidzas transportlīdzekļa pašreizējā OCTA polise, ja tāda ir." } },
    },
    {
      selector: '[data-help="offer-insurers-panel"]',
      title: "Choose the insurers",
      body: 'Tick the insurers to ask for a quote, or use "Select All" / "Clear All". At least one is required. Clicking a logo ticks it too.',
      section: "Insurance and insurers",
      context: "new-offer-tab",
      i18n: { lv: { title: "Izvēlēties apdrošinātājus", body: "Atzīmējiet apdrošinātājus, kuriem prasīt kotāciju, vai izmantojiet “Atlasīt visu” / “Notīrīt visu”. Vajadzīgs vismaz viens. Klikšķis uz logotipa to arī atzīmē." } },
    },
    {
      selector: '[data-help="offer-periods-panel"]',
      title: "Choose the MTPL periods",
      body: "MTPL only: the policy lengths to quote (1, 3, 6, 9 or 12 months). You get one premium per insurer and period.",
      section: "Insurance and insurers",
      context: "new-offer-tab",
      i18n: { lv: { title: "Izvēlēties OCTA periodus", body: "Tikai OCTA: polišu ilgumi, kuriem prasīt kotāciju (1, 3, 6, 9 vai 12 mēneši). Jūs saņemat vienu prēmiju katram apdrošinātājam un periodam." } },
    },
    {
      selector: '[data-help="offer-payments-panel"]',
      title: "Choose the CASCO payment plans",
      body: "CASCO only: how many instalments to quote (1, 2, 4 or 12 payments). You get a premium per insurer, variant and payment plan.",
      section: "Insurance and insurers",
      context: "casco-new-offer-tab",
      i18n: { lv: { title: "Izvēlēties KASKO maksājumu plānus", body: "Tikai KASKO: cik maksājumos prasīt kotāciju (1, 2, 4 vai 12 maksājumi). Jūs saņemat prēmiju katram apdrošinātājam, variantam un maksājumu plānam." } },
    },
    {
      selector: '[data-help="offer-preview-button"]',
      title: "Preview quotes",
      body: "Asks the insurers for prices without saving anything — a quick check before creating the offer. Missing or invalid fields are listed and marked on General Information instead.",
      section: "Insurance and insurers",
      context: "new-offer-tab",
      i18n: { lv: { title: "Priekšskatīt kotācijas", body: "Pieprasa apdrošinātājiem cenas, neko nesaglabājot, — ātra pārbaude pirms piedāvājuma izveides. Trūkstošie vai nederīgie lauki tā vietā tiek uzskaitīti un iezīmēti cilnē “Vispārīgā informācija”." } },
    },
    {
      selector: '[data-help="offer-create-offer-button"]',
      title: "Create the offer",
      body: "Requests the quotes, saves the offer and generates its PDF and Word documents. The offer gets its number and the vehicle, client and start date are locked from then on.",
      section: "Insurance and insurers",
      context: "new-offer-tab",
      i18n: { lv: { title: "Izveidot piedāvājumu", body: "Pieprasa kotācijas, saglabā piedāvājumu un izveido tā PDF un Word dokumentus. Piedāvājums saņem numuru, un no šī brīža transportlīdzeklis, klients un sākuma datums ir bloķēti." } },
    },

    // --- Quotes -------------------------------------------------------------------------
    {
      selector: '[data-help="offer-quotes-table"]',
      title: "Compare the quotes",
      body: "One column per insurer. MTPL shows a premium per period; CASCO shows variants with their deductibles and a premium per payment plan - always two per insurer, even when the insurer returned fewer or none, so missing values can be entered and an unused variant excluded with its checkbox. Rows for warnings and errors explain a missing price. Select the premium the client chose with its radio button. Until an invoice exists, the pencil on a value lets you override it and the checkbox in an insurer's header excludes it from the offer.",
      section: "Quotes",
      context: "created-offer",
      optional: true,
      i18n: { lv: { title: "Salīdzināt kotācijas", body: "Viena kolonna katram apdrošinātājam. OCTA rāda prēmiju katram periodam; KASKO rāda variantus ar to pašriskiem un prēmiju katram maksājumu plānam - vienmēr divus katram apdrošinātājam, arī ja apdrošinātājs atgrieza mazāk vai nevienu, tāpēc trūkstošās vērtības var ievadīt, bet neizmantotu variantu izslēgt ar tā izvēles rūtiņu. Brīdinājumu un kļūdu rindas paskaidro, kāpēc cenas nav. Ar radio pogu atlasiet klienta izvēlēto prēmiju. Kamēr nav rēķina, zīmulis pie vērtības ļauj to aizstāt, un izvēles rūtiņa apdrošinātāja virsrakstā izslēdz to no piedāvājuma." } },
    },
    {
      selector: '[data-help="offer-update-button"]',
      title: "Update the offer",
      body: "Appears when you have edited premiums or deductibles or excluded insurers. Saves those changes to the offer and regenerates its documents.",
      section: "Quotes",
      context: "quote-edits",
      optional: true,
      i18n: { lv: { title: "Atjaunināt piedāvājumu", body: "Parādās, ja esat rediģējis prēmijas vai pašriskus vai izslēdzis apdrošinātājus. Saglabā šīs izmaiņas piedāvājumā un no jauna izveido tā dokumentus." } },
    },
    {
      selector: '[data-help="offer-save-offer-button"]',
      title: "Save the offer with the insurers",
      body: "MTPL only: saves the selected quotes with the insurers. The saved offer documents appear and each quote shows the policy number returned by the insurer.",
      section: "Quotes",
      context: "mtpl-not-invoiced",
      optional: true,
      i18n: { lv: { title: "Saglabāt piedāvājumu pie apdrošinātājiem", body: "Tikai OCTA: saglabā atlasītās kotācijas pie apdrošinātājiem. Parādās saglabātie piedāvājuma dokumenti, un katrā kotācijā redzams apdrošinātāja atgrieztais polises numurs." } },
    },

    // --- Documents and e-mail ----------------------------------------------------------
    {
      selector: '[data-help="offer-documents"]',
      title: "Open the offer documents",
      body: "The offer as PDF and as an editable Word file, and later the invoice and group invoice documents. Each opens in a new tab. Below them you see who e-mailed the offer, to whom and when.",
      section: "Documents and e-mail",
      context: "created-offer",
      optional: true,
      i18n: { lv: { title: "Atvērt piedāvājuma dokumentus", body: "Piedāvājums PDF formātā un kā rediģējams Word fails, vēlāk arī rēķina un grupas rēķina dokumenti. Katrs atveras jaunā cilnē. Zem tiem redzams, kas, kam un kad nosūtīja piedāvājumu pa e-pastu." } },
    },
    {
      selector: '[data-help="offer-send-offer-email"]',
      title: "E-mail the offer",
      body: "Opens a pre-filled e-mail to the client with the offer documents attached. Hidden once an invoice exists.",
      section: "Documents and e-mail",
      context: "offer-not-invoiced",
      optional: true,
      i18n: { lv: { title: "Nosūtīt piedāvājumu pa e-pastu", body: "Atver iepriekš aizpildītu e-pastu klientam ar pievienotiem piedāvājuma dokumentiem. Nav redzams, kad izveidots rēķins." } },
    },
    {
      selector: '[data-help="offer-email-to"]',
      title: "Recipient",
      body: "Required. The client's e-mail is filled in; change it to send elsewhere. Add copies in CC.",
      section: "Documents and e-mail",
      context: "email-dialog",
      i18n: { lv: { title: "Adresāts", body: "Obligāti. Aizpildīts klienta e-pasts; mainiet to, lai sūtītu citur. Kopijas pievienojiet laukā CC." } },
    },
    {
      selector: '[data-help="offer-email-subject"]',
      title: "Subject",
      body: "Required. Pre-filled from the template; adjust it if needed.",
      section: "Documents and e-mail",
      context: "email-dialog",
      i18n: { lv: { title: "Temats", body: "Obligāti. Aizpildīts no veidnes; ja vajag, pielāgojiet to." } },
    },
    {
      selector: '[data-help="offer-email-body"]',
      title: "Message",
      body: "Required. Pre-filled from the template with the client's name, the vehicle and plate, and your name. Edit freely; untick attachments you do not want to send or add more files above.",
      section: "Documents and e-mail",
      context: "email-dialog",
      i18n: { lv: { title: "Ziņojums", body: "Obligāti. Aizpildīts no veidnes ar klienta vārdu, transportlīdzekli un numura zīmi un jūsu vārdu. Rediģējiet brīvi; augstāk noņemiet atzīmi pielikumiem, kurus nevēlaties sūtīt, vai pievienojiet citus failus." } },
    },
    {
      selector: '[data-help="offer-email-send"]',
      title: "Send the e-mail",
      body: "Sends the e-mail from your name and records it on the offer; the status becomes Offer Sent.",
      section: "Documents and e-mail",
      context: "email-dialog",
      i18n: { lv: { title: "Nosūtīt e-pastu", body: "Nosūta e-pastu jūsu vārdā un reģistrē to piedāvājumā; statuss kļūst “Piedāvājums nosūtīts”." } },
    },

    // --- Invoice ----------------------------------------------------------------------------
    {
      selector: '[data-help="offer-create-invoice-button"]',
      title: "Create the invoice",
      body: 'Enabled after you select a premium in the quotes table ("Please select a quote first" otherwise). Hidden while quote changes wait for "Update Offer", and once an invoice exists.',
      section: "Invoice",
      context: "offer-not-invoiced",
      optional: true,
      i18n: { lv: { title: "Izveidot rēķinu", body: "Pieejams pēc tam, kad kotāciju tabulā atlasāt prēmiju (citādi redzams “Please select a quote first”). Nav redzams, kamēr kotāciju izmaiņas gaida “Atjaunināt piedāvājumu”, un kad rēķins jau izveidots." } },
    },
    {
      selector: '[data-help="offer-invoice-due-date"]',
      title: "Due date",
      body: "Required. The date the client must pay by; it cannot be in the past.",
      section: "Invoice",
      context: "invoice-dialog",
      i18n: { lv: { title: "Apmaksas termiņš", body: "Obligāti. Datums, līdz kuram klientam jāsamaksā; tas nevar būt pagātnē." } },
    },
    {
      selector: '[data-help="offer-invoice-doc-number"]',
      title: "Document number",
      body: "Required. Pick a template (offer ID or policy number) to fill it, or type your own. The invoice number itself is assigned when the invoice is created (Q + date + sequence number); pick \"Invoice Number\" to print it as the document number too.",
      section: "Invoice",
      context: "invoice-dialog",
      i18n: { lv: { title: "Dokumenta numurs", body: "Obligāti. Izvēlieties veidni (piedāvājuma ID vai polises numurs), lai to aizpildītu, vai ierakstiet savu. Pašu rēķina numuru piešķir, veidojot rēķinu (Q + datums + kārtas numurs); izvēlieties “Rēķina numurs”, lai to drukātu arī kā dokumenta numuru." } },
    },
    {
      selector: '[data-help="offer-invoice-premium"]',
      title: "Premium and discount",
      body: "The premium to invoice (for CASCO: per instalment). Enter a discount as a percentage or an amount — the other one and the total are recalculated.",
      section: "Invoice",
      context: "invoice-dialog",
      i18n: { lv: { title: "Prēmija un atlaide", body: "Rēķinā iekļaujamā prēmija (KASKO — par vienu maksājumu). Ievadiet atlaidi procentos vai summā — otra vērtība un kopsumma tiek pārrēķinātas." } },
    },
    {
      selector: '[data-help="offer-invoice-payer"]',
      title: "Who pays the invoice",
      body: 'Required. "Same as client" addresses the invoice to the client; "Different payer" lets you search GOS or enter the payer by hand. The payer needs an e-mail address.',
      section: "Invoice",
      context: "invoice-dialog",
      i18n: { lv: { title: "Kas maksā rēķinu", body: "Obligāti. “Tāds pats kā klients” adresē rēķinu klientam; “Cits maksātājs” ļauj meklēt GOS vai ievadīt maksātāju manuāli. Maksātājam vajadzīga e-pasta adrese." } },
    },
    {
      selector: '[data-help="offer-invoice-create"]',
      title: "Create the invoice",
      body: "Generates the invoice PDF and Word file; the offer's status becomes Invoice and its quotes can no longer be edited. Send it with the envelope next to the invoice PDF.",
      section: "Invoice",
      context: "invoice-dialog",
      i18n: { lv: { title: "Izveidot rēķinu", body: "Izveido rēķina PDF un Word failu; piedāvājuma statuss kļūst “Rēķins”, un tā kotācijas vairs nevar rediģēt. Nosūtiet to ar aploksni blakus rēķina PDF." } },
    },
  ],
  i18n: {
    lv: {
      title: "Piedāvājums",
      tips: [
        "Kad piedāvājums ir izveidots, tā transportlīdzeklis, klients un sākuma datums ir bloķēti. Lai saņemtu kotāciju ar citiem datiem, piedāvājumu sarakstā izmantojiet “Dublēt piedāvājumu”.",
        "Kotāciju izmaiņas (rediģētas prēmijas, izslēgti apdrošinātāji) tiek saglabātas tikai pēc “Atjaunināt piedāvājumu”. Kamēr izmaiņas gaida, poga “Izveidot rēķinu” nav redzama.",
        "Kad rēķins ir izveidots, kotācijas vairs nevar rediģēt un piedāvājumu vairs nevar nosūtīt pa e-pastu no šejienes — tā vietā nosūtiet rēķinu.",
      ],
      intro:
        "Viens OCTA vai KASKO piedāvājums no sākuma līdz rēķinam. Cilnē \"Vispārīgā informācija\" aizpildiet transportlīdzekli un klientu (lielāko daļu izdara \"Aizpildīt\"), pēc tam cilnē \"Piedāvājums\" izvēlieties sākuma datumu un apdrošinātājus un nospiediet \"Izveidot piedāvājumu\", lai saņemtu kotācijas un dokumentus. Pēc tam varat pielāgot kotācijas, nosūtīt piedāvājumu pa e-pastu un izveidot rēķinu klienta izvēlētajai kotācijai.",
      sections: {
        "Getting around": "Orientēšanās",
        "Prefill the vehicle": "Transportlīdzekļa aizpildīšana",
        "Vehicle data": "Transportlīdzekļa dati",
        "Client data": "Klienta dati",
        "Insurance and insurers": "Apdrošināšana un apdrošinātāji",
        "Quotes": "Kotācijas",
        "Documents and e-mail": "Dokumenti un e-pasts",
        "Invoice": "Rēķins",
      },
      contexts: {
        "general-tab": "Atveriet cilni \"Vispārīgā informācija\".",
        "offer-tab": "Atveriet cilni \"Piedāvājums\".",
        "casco-general": "Tikai KASKO piedāvājumā, cilnē \"Vispārīgā informācija\".",
        "company-client": "Cilnē \"Vispārīgā informācija\" iestatiet klienta tipu uz uzņēmumu.",
        "person-client": "Cilnē \"Vispārīgā informācija\" iestatiet klienta tipu uz fizisku personu.",
        "new-offer-tab": "Tikai jaunam piedāvājumam pirms \"Izveidot piedāvājumu\", cilnē \"Piedāvājums\".",
        "mtpl-offer-tab": "Tikai OCTA piedāvājumā, cilnē \"Piedāvājums\".",
        "casco-new-offer-tab": "Tikai jaunam KASKO piedāvājumam pirms \"Izveidot piedāvājumu\", cilnē \"Piedāvājums\".",
        "created-offer": "Tikai izveidotā piedāvājumā, cilnē \"Piedāvājums\".",
        "offer-not-invoiced": "Tikai izveidotā piedāvājumā bez rēķina, cilnē \"Piedāvājums\".",
        "mtpl-not-invoiced": "Tikai izveidotā OCTA piedāvājumā bez rēķina, cilnē \"Piedāvājums\".",
        "quote-edits": "Izveidotā piedāvājumā bez rēķina cilnē \"Piedāvājums\" mainiet prēmiju vai izslēdziet apdrošinātāju.",
        "email-dialog": "Cilnē \"Piedāvājums\" noklikšķiniet uz aploksnes ikonas blakus piedāvājuma PDF.",
        "invoice-dialog": "Cilnē \"Piedāvājums\" kotāciju tabulā atlasiet prēmiju un noklikšķiniet uz \"Izveidot rēķinu\".",
      },
    },
  },
};
